import React, { useState, useEffect } from 'react';
import { useCreateAsset, useUpdateAsset, Asset, AssetMember } from '../hooks/useAssets.ts';
import { useAssetMembers } from '../hooks/useAssets.ts';
import { useGetProfile } from '../hooks/useProfile.ts';
import { X, Plus, Trash2 } from 'lucide-react';

interface AssetFormProps {
  asset?: Asset | null;
  onClose: () => void;
}

const AssetForm: React.FC<AssetFormProps> = ({ asset, onClose }) => {
  const [form, setForm] = useState({
    name: '',
    description: '',
    total_value: '',
    purchase_date: '',
    status: 'active',
    notes: '',
  });
  const [members, setMembers] = useState<AssetMember[]>([]);
  const [error, setError] = useState('');

  const createAsset = useCreateAsset();
  const updateAsset = useUpdateAsset();
  const { data: membersData } = useAssetMembers();
  const { data: profileData } = useGetProfile();

  const availableMembers = membersData?.data || [];
  const groupId = profileData?.data?.group_id;

  useEffect(() => {
    if (asset) {
      setForm({
        name: asset.name || '',
        description: asset.description || '',
        total_value: asset.total_value?.toString() || '',
        purchase_date: asset.purchase_date || '',
        status: asset.status || 'active',
        notes: asset.notes || '',
      });
      if (asset.members && asset.members.length > 0) {
        setMembers(asset.members.map(m => ({
          member_id: m.member_id,
          percentage: Number(m.percentage),
        })));
      }
    }
  }, [asset]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleMemberChange = (index: number, field: string, value: any) => {
    const updatedMembers = [...members];
    updatedMembers[index] = { ...updatedMembers[index], [field]: value };
    setMembers(updatedMembers);
  };

  const getAvailableMembersForRow = (currentIndex: number) => {
    const selectedMemberIds = members
      .map((member, index) => index !== currentIndex ? member.member_id : 0)
      .filter(id => id > 0);
    
    return availableMembers.filter(member => !selectedMemberIds.includes(member.id));
  };

  const addMemberRow = () => {
    setMembers([...members, { member_id: 0, percentage: 0 }]);
  };

  const removeMemberRow = (index: number) => {
    if (members.length > 0) {
      setMembers(members.filter((_, i) => i !== index));
    }
  };

  const selectedMembers = members.filter(m => Number(m.member_id) > 0);
  const calculateTotalPercentage = () => {
    return selectedMembers.reduce((sum, member) => sum + Number(member.percentage || 0), 0);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!form.name || !form.total_value || !form.purchase_date) {
      setError('Name, total value, and purchase date are required.');
      return;
    }

    // Validate members
    const validMembers = selectedMembers;

    // Check for duplicate members
    const memberIds = validMembers.map(m => m.member_id);
    const uniqueMemberIds = [...new Set(memberIds)];
    if (memberIds.length !== uniqueMemberIds.length) {
      setError('Each member can only be assigned once to the asset.');
      return;
    }

    // Check if all members have valid data
    for (const member of validMembers) {
      if (!member.percentage || member.percentage <= 0) {
        setError('All members must have a valid percentage greater than 0.');
        return;
      }
    }

    const payload = {
      ...form,
      group_id: groupId,
      total_value: Number(form.total_value),
      members: validMembers,
    };

    if (asset) {
      updateAsset.mutate(
        { id: asset.id, ...payload },
        { onSuccess: onClose }
      );
    } else {
      createAsset.mutate(payload, { onSuccess: onClose });
    }
  };

  const totalPercentage = calculateTotalPercentage();
  const isPercentageValid = totalPercentage >= 0 && totalPercentage <= 100;

  // console.log('members length (raw):', members.length, 'selected:', selectedMembers.length, selectedMembers);

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-bold text-gray-900">
          {asset ? 'Edit Asset' : 'Add New Asset'}
        </h2>
        <button
          type="button"
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600"
        >
          <X className="w-6 h-6" />
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Basic Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">Basic Information</h3>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Asset Name *
          </label>
          <input
            type="text"
            name="name"
            value={form.name}
            onChange={handleChange}
            required
            placeholder="e.g., Company Car, Office Equipment"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            name="description"
            value={form.description}
            onChange={handleChange}
            rows={3}
            placeholder="Describe the asset..."
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Total Value (KSh) *
            </label>
            <input
              type="number"
              name="total_value"
              value={form.total_value}
              onChange={handleChange}
              required
              min="0"
              step="0.01"
              placeholder="0.00"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Purchase Date *
            </label>
            <input
              type="date"
              name="purchase_date"
              value={form.purchase_date}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              name="status"
              value={form.status}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="active">Active</option>
              <option value="sold">Sold</option>
              <option value="disposed">Disposed</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Notes
          </label>
          <textarea
            name="notes"
            value={form.notes}
            onChange={handleChange}
            rows={2}
            placeholder="Additional notes..."
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Member Ownership */}
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-900">Member Ownership</h3>
          <div className="flex items-center gap-2">
            <span className={`text-sm font-medium ${isPercentageValid ? 'text-green-600' : 'text-red-600'}`}>
              Total: {totalPercentage.toFixed(2)}%
            </span>
            <button
              type="button"
              onClick={addMemberRow}
              className="flex items-center gap-1 text-blue-600 hover:text-blue-700 text-sm font-medium"
            >
              <Plus className="w-4 h-4" /> Add Member
            </button>
          </div>
        </div>

        <div className="space-y-3">
          {members.map((member, index) => (
            <div key={index} className="flex gap-2 items-start">
              <div className="flex-1">
                <select
                  value={member.member_id}
                  onChange={(e) => handleMemberChange(index, 'member_id', Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  <option value="0">Select Member</option>
                  {getAvailableMembersForRow(index).map((m: any) => (
                    <option key={m.id} value={m.id}>
                      {m.name} ({m.phone_number})
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex-1">
                <input
                  type="number"
                  value={member.percentage}
                  onChange={(e) => handleMemberChange(index, 'percentage', (e.target.value))}
                  placeholder="% Ownership"
                  min="0"
                  max="100"
                  step="0.01"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              {selectedMembers.length > 0 && (
                <button
                  type="button"
                  onClick={() => removeMemberRow(index)}
                  className="text-red-600 hover:text-red-700 p-2"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              )}
            </div>
          ))}
        </div>

        {!isPercentageValid && totalPercentage > 0 && (
          <div className="text-sm text-red-600">
            Total ownership percentage must be between 0% and 100%. Currently {totalPercentage.toFixed(2)}%
          </div>
        )}
      </div>

      {/* Form Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={createAsset.isPending || updateAsset.isPending}
          className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {createAsset.isPending || updateAsset.isPending
            ? 'Saving...'
            : asset
            ? 'Update Asset'
            : 'Create Asset'}
        </button>
      </div>
    </form>
  );
};

export default AssetForm;

